import React, { useEffect, useState, useMemo, useRef } from "react";
import {
  Layout,
  Button,
  Typography,
  Dropdown,
  Divider,
  Flex,
  List,
  Modal,
  MenuProps,
  Radio,
} from "antd";
import { FolderOutlined, PlusOutlined } from "@ant-design/icons";
import { toast } from "react-toastify";
import { BsFilterLeft } from "react-icons/bs";
import { useSelector } from "react-redux";
import { useChatSidebarContext } from "../../../context/ChatSidebarScreenContext";
import {
  formatChats,
  formatMessage,
  formatSingleChat,
  sortChats,
} from "../../../utils";
import { useSocket } from "../../../context/SocketContext";
import {
  chatApi,
  useGetConversationListQuery,
  useMuteConversationMutation,
  usePinConversationMutation,
  useArchiveConversationMutation,
  useDeleteConversationMutation,
} from "../../../redux/services/chatsApi";
import { useAppDispatch } from "../../../redux/utils/Dispatcher";
import {
  archiveChat,
  setInitialUnreadCount,
  deleteChat,
  moveChatToTop,
  muteChat,
  pinChat,
  setChats,
  setCurrentChat,
  unarchiveChat,
  unmuteChat,
  unpinChat,
  updateConversationList,
  updateCurrentChat,
  setLastReadTimestamps,
} from "../../../redux/slices/chatSlice";
import { RootState } from "../../../redux/store";
import NewChatSection from "../../../pages/Chat/NewChat";
import CustomLoader from "../../../components/common/UI/Loader";
import ArchivedChats from "../../../pages/Chat/ArchivedChats";
import ChatCard from "../../../components/Chat/ChatCard";
import ENDPOINTS from "../../../services/endpoint";
import apiInstance from "../../../services/apiService";
import GradientButton from "../../../components/common/UI/GradientButton";
import GroupInfo from "../../../pages/Chat/GroupInfo";
import ForwardMessage from "../../../pages/Chat/ForwardMessage";
import SearchInput from "../../../components/common/UI/SearchInput";
import BroadCastInfo from "../../../pages/Chat/BroadCastInfo";
import ContactList from "../../../components/common/ContactList";
import { storageService } from "../../../services/storageService";
import conversationCacheDB, {
  CachedConversation,
} from "../../../services/conversationCacheDB";
import { normaliseDelta } from "../../../services/deltaSync";
import { messageGraph } from "../../../services/messageGraph";
import { FEATURE_FLAGS } from "../../../constants";
import "./index.scss";

const { Sider } = Layout;
const { Text, Title } = Typography;
const FilterIcon = BsFilterLeft as unknown as React.FC;

// ── Helper: map a formatted chat object → CachedConversation ──────────────

function toCached(chat: any, lastUpdated: string): CachedConversation {
  return {
    id: chat.id || "",
    name: chat.name || "",
    number: chat.number ?? null,
    isGroup: chat.isGroup ?? false,
    isBroadCast: chat.isBroadCast ?? false,
    iseFone: chat.iseFone ?? false,
    sourceNumber: chat.sourceNumber || "",
    image: chat.image,
    groupMembers: chat.groupMembers,
    broadCastMembers: chat.broadCastMembers,
    groupSettings: chat.groupSettings,
    savedName: chat.savedName,
    isMuted: chat.isMuted ?? false,
    isPin: chat.isPin ?? false,
    isArchived: chat.isArchived ?? false,
    isBlocked: chat.isBlocked ?? false,
    message: chat.message || "",
    mediaType: chat.mediaType ?? 0,
    time: chat.time || "",
    date: chat.date || "",
    unreadCount: chat.unreadCount ?? 0,
    lastUpdateToSort: chat.lastUpdateToSort || lastUpdated,
    lastReadAt: chat.lastReadAt,
    lastUpdated,
  };
}

// ── Component ──────────────────────────────────────────────────────────────

const ChatSidebar: React.FC = () => {
  const dispatch = useAppDispatch();
  const { currentChat } = useSelector((state: RootState) => state.chat);
  const { socket } = useSocket();
  const { currentScreen, setCurrentScreen } = useChatSidebarContext();
  const { contacts } = useSelector((state: RootState) => state.contacts);
  const { chats, archivedChats } = useSelector(
    (state: RootState) => state.chat,
  );
  const userId = storageService.getUserId();

  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(false);
  const [showBlockModal, setShowBlockModal] = useState<boolean>(false);
  const [isChatDeleteModalVisible, setIsChatDeleteModalVisible] =
    useState<boolean>(false);
  const [chatsFilterValue, setChatsFilterValue] = useState("all");
  const [tabPosition, setTabPosition] = useState<any>("all");
  const [showContactList, setShowContactList] = useState(false);

  // ── RTK Query ────────────────────────────────────────────────────────────

  const queryParams = useMemo(() => {
    const params: Record<string, any> = {};
    if (chatsFilterValue === "efone") params.iseFone = "1";
    else if (chatsFilterValue === "non-efone") params.iseFone = "0";
    else if (chatsFilterValue === "isGroup") params.isGroup = "1";
    else if (chatsFilterValue === "isBroadCast") params.isBroadCast = "1";
    return params;
  }, [chatsFilterValue]);

  const { data: conversationData, isFetching: conversationsLoading } =
    useGetConversationListQuery(queryParams, {
      skip: currentScreen !== "chats" && currentScreen !== "archived",
      refetchOnMountOrArgChange: true,
    });

  const [muteConv] = useMuteConversationMutation();
  const [pinConv] = usePinConversationMutation();
  const [archiveConv] = useArchiveConversationMutation();
  const [deleteConv] = useDeleteConversationMutation();

  // ── Stable refs (avoid stale closures in socket handlers) ────────────────

  const chatsRef = useRef(chats);
  const archivedChatsRef = useRef(archivedChats);
  const currentChatRef = useRef(currentChat);
  const userIdRef = useRef(userId);

  useEffect(() => {
    chatsRef.current = chats;
  }, [chats]);
  useEffect(() => {
    archivedChatsRef.current = archivedChats;
  }, [archivedChats]);
  useEffect(() => {
    currentChatRef.current = currentChat;
  }, [currentChat]);
  useEffect(() => {
    userIdRef.current = userId;
  }, [userId]);

  // Load IDB once per userId — no contacts dependency to avoid double-dispatch.
  useEffect(() => {
    if (!userId) return;

    conversationCacheDB
      .getAll()
      .then((cached) => {
        if (!cached.length) return;
        const sorted = sortChats([...cached]);
        dispatch(
          setChats({
            chats: sorted.filter((c) => !c.isArchived),
            archivedChats: sorted.filter((c) => c.isArchived),
          }),
        );
        // Populate the lastReadAt lookup table from IDB so ChatRightSection
        // can place the unread divider deterministically on first open.
        const timestamps: Record<string, string> = {};
        cached.forEach((c) => {
          if (c.lastReadAt) timestamps[c.id] = c.lastReadAt;
        });
        if (Object.keys(timestamps).length) {
          dispatch(setLastReadTimestamps(timestamps));
        }
      })
      .catch(() => {});
  }, [userId, dispatch]);

  // Patch savedName when contacts load — targeted per-chat updates, O(n+m) via Map.
  // Uses updateConversationList (not setChats) to avoid replacing the whole array
  // and racing with concurrent socket-driven unreadCount updates.
  // updateConversationList only searches state.chats, so archived chats only get
  // the IDB write (their name in the archived list updates on next IDB reload).
  useEffect(() => {
    if (!contacts?.length) return;

    // O(m): phone → {chat, isArchived} lookup
    const chatsByPhone = new Map<string, { chat: any; isArchived: boolean }>();
    chatsRef.current.forEach((c: any) => {
      if (!c.isGroup && !c.isBroadCast && c.sourceNumber) {
        chatsByPhone.set(c.sourceNumber, { chat: c, isArchived: false });
      }
    });
    archivedChatsRef.current.forEach((c: any) => {
      if (!c.isGroup && !c.isBroadCast && c.sourceNumber) {
        chatsByPhone.set(c.sourceNumber, { chat: c, isArchived: true });
      }
    });

    if (!chatsByPhone.size) return;

    const now = new Date().toISOString();
    contacts.forEach((contact: any) => {
      if (!contact.phoneNumber || !contact.fullName) return;
      const entry = chatsByPhone.get(contact.phoneNumber);
      if (!entry || entry.chat.savedName === contact.fullName) return;

      const patched = { ...entry.chat, savedName: contact.fullName };

      if (!entry.isArchived) {
        // Targeted Redux update — only touches savedName, leaves unreadCount alone.
        dispatch(
          updateConversationList({ id: patched.id, savedName: contact.fullName }),
        );
      }
      // Persist to IDB for both regular and archived chats.
      conversationCacheDB.put(toCached(patched, now));
    });
  }, [contacts, dispatch]);

  useEffect(() => {
    if (!conversationData) return;

    const serverList: any[] =
      formatChats(conversationData?.newCnv?.docs[0]?.conversationList) ?? [];

    // Apply contact name overrides
    const withNames = serverList.map((convo: any) => {
      const contact = contacts?.find(
        (c: any) => c.phoneNumber === convo.sourceNumber,
      );
      return contact?.fullName
        ? { ...convo, savedName: contact.fullName }
        : convo;
    });

    // Merge: current Redux state takes precedence for volatile fields if newer.
    // This prevents an RTK refetch from overwriting socket-received messages.
    const currentReduxMap = new Map(
      chatsRef.current.map((c: any) => [c.id, c]),
    );

    const merged = withNames.map((serverChat: any) => {
      const reduxChat = currentReduxMap.get(serverChat.id);
      if (!reduxChat) return serverChat;

      const isActiveChat = currentChatRef.current?.id === serverChat.id;

      const serverTs = serverChat.lastUpdateToSort
        ? new Date(serverChat.lastUpdateToSort).getTime()
        : 0;
      const localTs = reduxChat.lastUpdateToSort
        ? new Date(reduxChat.lastUpdateToSort).getTime()
        : 0;

      if (localTs > serverTs) {
        // Local Redux (from socket) is newer — keep its volatile fields,
        // use server data for everything else (stable fields stay fresh).
        return {
          ...serverChat,
          message: reduxChat.message,
          mediaType: reduxChat.mediaType,
          time: reduxChat.time,
          date: reduxChat.date,
          unreadCount: reduxChat.unreadCount,
          lastUpdateToSort: reduxChat.lastUpdateToSort,
        };
      }

      // Server wins for timestamp, but never let a refetch overwrite the
      // locally-managed unread count for the currently open chat.
      if (isActiveChat) {
        return { ...serverChat, unreadCount: reduxChat.unreadCount };
      }

      return serverChat;
    });

    const sorted = sortChats(merged);
    dispatch(
      setChats({
        chats: sorted.filter((c: any) => !c.isArchived),
        archivedChats: sorted.filter((c: any) => c.isArchived),
      }),
    );

    // Persist merged result to IDB.
    // batchPut skips any entry where the IDB copy is already newer
    // (protects against server overwriting socket-received data).
    const now = new Date().toISOString();
    conversationCacheDB
      .batchPut(merged.map((c: any) => toCached(c, now)))
      .catch(() => {});
  }, [contacts, conversationData, dispatch]);

  // ── Socket handlers ───────────────────────────────────────────────────────

  useEffect(() => {
    if (!socket) return;

    const handleReceiveMessage = (msg: any) => {
      // Phase 3: normalise socket payload via delta sync (flag-gated).
      // When USE_DELTA_SYNC is true the destructure comes from normaliseDelta;
      // when false the original paths are used, so behaviour is unchanged.
      let rawConversation = Array.isArray(msg.conversation)
        ? msg.conversation[0]
        : msg.conversation;
      let incomingMsg = msg.newMessage ?? msg.messageList?.[0];

      if (FEATURE_FLAGS.USE_DELTA_SYNC) {
        const delta = normaliseDelta(msg);
        if (!delta) return;
        rawConversation = delta.conversation;
        incomingMsg = delta.message;
      }

      const conversation = rawConversation;
      if (!conversation) return;

      const formattedConvo = formatSingleChat(conversation);
      const newMsg = formatMessage(incomingMsg, userIdRef.current);
      const chatToUpdate =
        chatsRef.current.find((item: any) => item.id === formattedConvo.id) ||
        archivedChatsRef.current.find(
          (item: any) => item.id === formattedConvo.id,
        );
      const isArchivedChat = Boolean(chatToUpdate?.isArchived);

      if (FEATURE_FLAGS.USE_CRDT_ENGINE && newMsg?.id) {
        messageGraph.mirror(formattedConvo.id, newMsg);
      }

      const senderId = incomingMsg?.ofUser || formattedConvo.ofUser;
      const isSelf = senderId
        ? String(senderId) === String(userIdRef.current)
        : false;
      const isActiveChat = currentChatRef.current?.id === formattedConvo.id;

      // For active chat, unreadCount is managed exclusively by ChatRightSection
      // (synced via dispatch when user is scrolled up, cleared on scroll to bottom).
      // The sidebar must NOT increment it — that would double-count.
      let newUnreadCount: number | undefined;
      if (isSelf) {
        newUnreadCount = 0;
      } else if (isActiveChat) {
        // Active chat — leave unreadCount untouched; ChatRightSection owns it.
        newUnreadCount = undefined;
      } else {
        newUnreadCount = (chatToUpdate?.unreadCount || 0) + 1;
      }

      // For the sidebar preview, use the conversation's formatted message as the
      // source of truth — formatSingleChat always maps mediaType correctly
      // ("Sent a Photo", "Shared an audio", etc.). Only override with the raw
      // message content for plain-text messages (mediaType === 0).
      const sidebarMessage =
        formattedConvo.mediaType === 0 && newMsg?.lastMessage
          ? newMsg.lastMessage
          : formattedConvo.message;

      // Always strip the unreadCount from the server payload if it's the active chat.
      // ChatRightSection manages the live unread count for the active chat based on
      // viewport position; the server's count in this socket event is often 0
      // (if it thinks we're looking) which would overwrite our local increment.
      if (isActiveChat) {
        delete formattedConvo.unreadCount;
      }

      const updatePayload: any = {
        ...formattedConvo,
        message: sidebarMessage,
        ...(newUnreadCount !== undefined
          ? { unreadCount: newUnreadCount }
          : {}),
        time: newMsg?.time || formattedConvo.time,
        date: newMsg?.date || formattedConvo.date,
        lastUpdateToSort: newMsg?.createdAt || formattedConvo.lastUpdateToSort,
      };

      if (incomingMsg?.ofUser !== undefined) {
        updatePayload.ofUser = incomingMsg.ofUser;
      }

      // Defensive check: don't overwrite a good groupMembers list with an
      // incomplete one that excludes the current user.
      if (formattedConvo.groupMembers && chatToUpdate?.groupMembers) {
        const amIInNewList = formattedConvo.groupMembers.some(
          (m: any) => String(m._id) === String(userIdRef.current),
        );
        const wasIInOldList = chatToUpdate.groupMembers.some(
          (m: any) => String(m._id) === String(userIdRef.current),
        );
        if (wasIInOldList && !amIInNewList) {
          delete updatePayload.groupMembers;
        }
      }

      // ── Redux update (instant UI) ─────────────────────────────────────
      dispatch(updateConversationList(updatePayload));
      if (!isArchivedChat) {
        dispatch(moveChatToTop({ id: formattedConvo.id }));
      }

      // ── IDB write-through (persist for next refresh) ──────────────────
      // Build a full CachedConversation by merging the existing Redux entry
      // (stable fields) with the incoming socket update (volatile fields).
      const now = new Date().toISOString();
      const base: any = chatToUpdate || {};
      conversationCacheDB.put({
        id: formattedConvo.id,
        name: base.name || formattedConvo.name || "",
        number: base.number ?? formattedConvo.number ?? null,
        isGroup: base.isGroup ?? formattedConvo.isGroup ?? false,
        isBroadCast: base.isBroadCast ?? formattedConvo.isBroadCast ?? false,
        iseFone: base.iseFone ?? formattedConvo.iseFone ?? false,
        sourceNumber: base.sourceNumber || formattedConvo.sourceNumber || "",
        image: base.image || formattedConvo.image,
        groupMembers:
          updatePayload.groupMembers ??
          base.groupMembers ??
          formattedConvo.groupMembers,
        broadCastMembers:
          base.broadCastMembers || formattedConvo.broadCastMembers,
        groupSettings: base.groupSettings || formattedConvo.groupSettings,
        savedName: base.savedName || formattedConvo.savedName,
        isMuted: base.isMuted ?? false,
        isPin: base.isPin ?? false,
        isArchived: base.isArchived ?? false,
        isBlocked: base.isBlocked ?? false,
        message: sidebarMessage || "",
        mediaType: updatePayload.mediaType ?? formattedConvo.mediaType ?? 0,
        time: updatePayload.time || "",
        date: updatePayload.date || "",
        unreadCount: isActiveChat
          ? (chatToUpdate?.unreadCount ?? 0)
          : (newUnreadCount ?? chatToUpdate?.unreadCount ?? 0),
        lastUpdateToSort: updatePayload.lastUpdateToSort || now,
        lastUpdated: now,
      });
    };

    const handleUserUpdates = (msg: any) => {
      const updatedConversation = formatSingleChat(msg?.conversation?.[0]);
      const chatToUpdate = chatsRef.current.find(
        (item: any) => item.id === updatedConversation.id,
      );

      // Defensive groupMembers check
      if (updatedConversation.groupMembers && chatToUpdate?.groupMembers) {
        const amIInNewList = updatedConversation.groupMembers.some(
          (m: any) => String(m._id) === String(userIdRef.current),
        );
        const wasIInOldList = chatToUpdate.groupMembers.some(
          (m: any) => String(m._id) === String(userIdRef.current),
        );
        if (wasIInOldList && !amIInNewList) {
          delete updatedConversation.groupMembers;
        }
      }

      // ── Redux update ──────────────────────────────────────────────────
      dispatch(updateConversationList(updatedConversation));
      if (updatedConversation.id === currentChatRef.current?.id) {
        dispatch(updateCurrentChat(updatedConversation));
      }

      // ── IDB write-through ─────────────────────────────────────────────
      if (chatToUpdate) {
        const now = new Date().toISOString();
        conversationCacheDB.put(
          toCached({ ...chatToUpdate, ...updatedConversation }, now),
        );
      }
    };

    socket.on("receive-message", handleReceiveMessage);
    socket.on("user-updates", handleUserUpdates);

    return () => {
      socket.off("receive-message", handleReceiveMessage);
      socket.off("user-updates", handleUserUpdates);
    };
  }, [socket, dispatch]);

  // ── UI actions ────────────────────────────────────────────────────────────

  const items: MenuProps["items"] = [
    { key: "all", label: "All" },
    { key: "efone", label: "Efone" },
    { key: "non-efone", label: "Non Efone" },
  ];

  const handleNewChatClick = () => setCurrentScreen("newChat");
  const handleBackToChats = () => setCurrentScreen("chats");
  const handleBlockClick = () => setShowBlockModal(true);

  const handleTabChange = (e: any) => {
    if (e.target.value === "contacts") {
      setShowContactList(true);
    } else {
      setShowContactList(false);
      setChatsFilterValue(e.target.value);
    }
    setTabPosition(e.target.value);
  };

  const muteConversations = async (chat: any) => {
    try {
      await muteConv({
        chatId: chat.id,
        mute: chat.isMuted ? "2" : "1",
      }).unwrap();
      if (chat.isMuted) dispatch(unmuteChat(chat.id));
      else dispatch(muteChat(chat.id));
    } catch (err: any) {
      toast.error(
        err.data?.message || err.msg || "Failed to mute. Please try again",
      );
    }
  };

  const pinConversations = async (chat: any) => {
    try {
      await pinConv({ chatId: chat.id, pin: chat.isPin ? "2" : "1" }).unwrap();
      if (chat.isPin) dispatch(unpinChat(chat?.id));
      else dispatch(pinChat(chat?.id));
    } catch (err: any) {
      toast.error(
        err.data?.message || err.msg || "Failed to pin. Please try again",
      );
    }
  };

  const archiveConversations = async (chat: any) => {
    try {
      await archiveConv({
        chatId: chat?.id,
        archive: chat.isArchived ? "2" : "1",
      }).unwrap();
      if (chat.isArchived) dispatch(unarchiveChat(chat.id));
      else dispatch(archiveChat(chat.id));
      dispatch(setCurrentChat(null));
    } catch (err: any) {
      toast.error(
        err.data?.message || err.msg || "Failed to Archive. Please try again",
      );
    }
  };

  const getPhoneNumberIdForUnblock = async (number: any) => {
    const encodedPhone = number.replace(/^\+/, "%2B");
    const URL = ENDPOINTS.BLOCK_NUMBER + "?phoneNumber=" + encodedPhone;
    const response = await apiInstance.get(URL);
    return response?.data.data?.blockedNumbers[0]?._id;
  };

  const handleBlockConversation = async () => {
    if (!currentChat) return;
    const isBlocked = currentChat?.isBlocked;
    setLoading(true);
    try {
      if (isBlocked) {
        const deletePhoneId = await getPhoneNumberIdForUnblock(
          currentChat?.sourceNumber,
        );
        await apiInstance.delete(`${ENDPOINTS.BLOCK_NUMBER}/${deletePhoneId}`);
      } else {
        await apiInstance.post(ENDPOINTS.BLOCK_NUMBER, {
          phoneNumber: currentChat?.sourceNumber,
          calls: true,
          sms: true,
        });
        toast.success("Chat blocked successfully!");
      }
      dispatch(updateCurrentChat({ isBlocked: !isBlocked }));
      dispatch(
        updateConversationList({ id: currentChat?.id, isBlocked: !isBlocked }),
      );
      setShowBlockModal(false);
    } catch (error: any) {
      toast.error(
        error?.message || "Something went wrong. Please try again later.",
      );
    } finally {
      setLoading(false);
    }
  };

  const deleteConversations = async () => {
    if (!currentChat?.id) {
      toast.error("Chat not found");
      return;
    }
    setLoading(true);
    try {
      await deleteConv({ chatId: currentChat.id }).unwrap();
      dispatch(
        chatApi.util.invalidateTags([{ type: "Messages", id: currentChat.id }]),
      );
      dispatch(deleteChat(currentChat.id));
      setIsChatDeleteModalVisible(false);
      toast.success("Conversation deleted successfully");
      dispatch(setCurrentChat(null));
    } catch (err: any) {
      toast.error(err.data?.message || err.message || "Could not delete Chat");
    } finally {
      setLoading(false);
    }
  };

  const updateUnreadCount = async (id: any) => {
    if (!id) return;
    try {
      await apiInstance.get(
        `${ENDPOINTS.GET_MESSAGE}?page=1&limit=20&chatId=${id}`,
      );
    } catch (err: any) {
      console.error("Error fetching conversations:", err);
    }
  };

  const handleCardClick = (id: string) => {
    const previousId = currentChat?.id;
    const clickedChat = (
      currentScreen === "archived" ? archivedChats : chats
    )?.find((c: any) => c?.id === id);

    if (!clickedChat) {
      toast.error("Chat not Found");
      return;
    }

    dispatch(setInitialUnreadCount(clickedChat?.unreadCount ?? 0));
    updateUnreadCount(previousId);
    dispatch(setCurrentChat(clickedChat));
  };

  const filteredConversations = useMemo(() => {
    const query = searchTerm.trim().toLowerCase();
    if (!query) return chats;
    return chats.filter((chat: any) => {
      const name =
        (chat?.isGroup || chat?.isBroadCast ? chat?.name : chat?.savedName) ||
        "";
      return (
        name.toLowerCase().includes(query) ||
        (chat?.sourceNumber || "").toLowerCase().includes(query)
      );
    });
  }, [chats, searchTerm]);

  useEffect(() => {
    if (currentChat?.isNewChat) {
      setChatsFilterValue("all");
      setTabPosition("all");
    }
  }, [currentChat]);

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <Sider className="chat-sidebar">
      {currentScreen === "chats" && (
        <div className="chats-screen-container">
          <div className="chat-header">
            <Flex align="center" justify="space-between">
              <Title className="main-title">Chats</Title>
              <GradientButton
                type="default"
                icon={<PlusOutlined />}
                onClick={handleNewChatClick}
              />
            </Flex>

            <Flex gap={10}>
              <SearchInput
                value={searchTerm}
                disabled={conversationsLoading}
                onChange={setSearchTerm}
                placeholder="Search..."
              />
              {chatsFilterValue !== "isGroup" &&
                chatsFilterValue !== "isBroadCast" && (
                  <Dropdown
                    overlayClassName="custom-dropdown"
                    trigger={["click"]}
                    menu={{ items, onClick: (e) => setChatsFilterValue(e.key) }}
                  >
                    <Button
                      disabled={conversationsLoading}
                      type="link"
                      className="filter-btn"
                    >
                      <FilterIcon />
                    </Button>
                  </Dropdown>
                )}
            </Flex>

            <Flex justify="center">
              <Radio.Group
                className="chat-filter-tabs"
                value={tabPosition}
                onChange={handleTabChange}
                disabled={conversationsLoading}
              >
                <Radio.Button value="all">All</Radio.Button>
                <Radio.Button value="contacts">Contacts</Radio.Button>
                <Radio.Button value="isGroup">Groups</Radio.Button>
                <Radio.Button value="isBroadCast">Broadcasts</Radio.Button>
              </Radio.Group>
            </Flex>
          </div>

          <div
            style={{
              display: showContactList ? "flex" : "none",
              flex: 1,
              flexDirection: "column",
              minHeight: 0,
            }}
          >
            <ContactList searchQuery={searchTerm} />
          </div>

          {!showContactList && (
            <>
              {archivedChats.length > 0 && (
                <div
                  className="archived-chats-bar"
                  onClick={() => {
                    if (!conversationsLoading) setCurrentScreen("archived");
                  }}
                >
                  <Flex align="center" gap={8}>
                    <FolderOutlined className="icon" />
                    <Text className="archived-title">Archived</Text>
                  </Flex>
                </div>
              )}

              <List
                className="chats-list"
                dataSource={filteredConversations}
                loading={{
                  spinning: conversationsLoading && chats.length === 0,
                  indicator: <CustomLoader />,
                }}
                renderItem={(item: any) => (
                  <div key={item.id}>
                    <ChatCard
                      item={item}
                      handleClick={handleCardClick}
                      handleBlock={() => handleBlockClick()}
                      handleDelete={() => setIsChatDeleteModalVisible(true)}
                      handleArchive={() => archiveConversations(item)}
                      handleMute={() => muteConversations(item)}
                      handlePin={() => pinConversations(item)}
                    />
                    <Divider className="divider" />
                  </div>
                )}
              />
            </>
          )}
        </div>
      )}

      {currentScreen === "archived" && (
        <ArchivedChats
          loading={conversationsLoading}
          handleBackToChats={handleBackToChats}
          handleCardClick={handleCardClick}
          handleBlockChat={handleBlockClick}
          handleDeleteConversations={() => setIsChatDeleteModalVisible(true)}
          handleArchiveConversations={archiveConversations}
          handleMuteConversations={muteConversations}
          handlePinConversations={pinConversations}
        />
      )}

      {currentScreen === "newChat" && (
        <NewChatSection handleBackToChats={handleBackToChats} />
      )}
      {currentScreen === "group-info" && <GroupInfo />}
      {currentScreen === "forward-msg" && <ForwardMessage />}
      {currentScreen === "broadcast-info" && <BroadCastInfo />}

      <Modal
        centered
        title={currentChat?.isBlocked ? "Confirm Unblock" : "Confirm Block"}
        open={showBlockModal}
        onCancel={() => setShowBlockModal(false)}
        footer={
          <>
            <Button
              className="modal-cancel-btn"
              type="text"
              onClick={() => setShowBlockModal(false)}
            >
              Cancel
            </Button>
            <Button
              className="modal-ok-btn danger"
              onClick={handleBlockConversation}
              loading={loading}
            >
              {currentChat?.isBlocked ? "Unblock" : "Block"}
            </Button>
          </>
        }
        className="custom-modal"
      >
        <p className="text-primary">
          Are you sure you want to{" "}
          {currentChat?.isBlocked ? "Unblock" : "Block"} this chat?
        </p>
      </Modal>

      <Modal
        centered
        className="custom-modal"
        title="Confirm Deletion"
        open={isChatDeleteModalVisible}
        onOk={deleteConversations}
        onCancel={() => setIsChatDeleteModalVisible(false)}
        okText="Delete"
        okButtonProps={{
          danger: true,
          loading: loading,
          className: "modal-ok-btn danger",
        }}
        cancelButtonProps={{ type: "text", className: "modal-cancel-btn" }}
        cancelText="Cancel"
      >
        <p className="text-primary">
          Are you sure you want to delete this chat?
        </p>
      </Modal>
    </Sider>
  );
};

export default ChatSidebar;
